import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from './components/NavBar';
import CategoriesSection from './components/CategoriesSection';
import HeroSection from './components/HeroSection';
import SmartImage from './components/SmartImage';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { COLLECTIONS } from './firebase/collections';
import { useCart } from './context/CartContext';

import {
  Wrench, ShoppingBag, Package, Settings, ArrowRight, CheckCircle, Home, Eye, Shield, Clock, TrendingUp, Award,
  ShoppingCart, Phone, Mail, MapPin
} from 'lucide-react';



const LuxuryCarWebsite = () => {

  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroData, setHeroData] = useState({
    title: 'الرفاهية تبدأ من هنا',
    subtitle: 'قطع غيار وإكسسوارات السيارات الفاخرة',
    description: 'نوفر لك أفضل قطع الغيار الأصلية والإكسسوارات الفاخرة لسيارتك مع خدمة عملاء استثنائية',
    buttonText: 'تصفح المنتجات',
    backgroundImages: [], // مصفوفة للصور بدلاً من صورة واحدة
    backgroundColor: 'from-gray-900 via-gray-800 to-black',
    slideInterval: 5000 // وقت التبديل بالميلي ثانية (5 ثواني افتراضياً)
  });
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    loadData();
  }, []);


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [categoriesSnapshot, productsSnapshot, servicesSnapshot, heroDoc] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.CATEGORIES)),
        getDocs(collection(db, COLLECTIONS.PRODUCTS)),
        getDocs(collection(db, COLLECTIONS.SERVICES)),
        getDoc(doc(db, 'settings', 'hero'))
      ]);

      const categoriesData = [];
      categoriesSnapshot.forEach((doc) => {
        categoriesData.push({ id: doc.id, ...doc.data() });
      });

      const productsData = [];
      productsSnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });

      const servicesData = [];
      servicesSnapshot.forEach((doc) => {
        servicesData.push({ id: doc.id, ...doc.data() });
      });

      if (heroDoc.exists()) {
        const heroDataFromDB = heroDoc.data();
        console.log('Hero data loaded from Firebase:', heroDataFromDB);
        setHeroData(heroDataFromDB);
      } else {
        console.log('No hero data found in Firebase');
      }

      setProducts(productsData);
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigation = [

    { id: 'home', name: 'الرئيسية', icon: <Home className="w-5 h-5" /> },

    { id: 'maintenance', name: 'الصيانة المنزلية', icon: <Wrench className="w-5 h-5" /> },

    { id: 'accessories', name: 'الاكسسوارات', icon: <ShoppingBag className="w-5 h-5" /> },

    { id: 'parts', name: 'قطع الغيار', icon: <Package className="w-5 h-5" /> },

    { id: 'modifications', name: 'التعديلات', icon: <Settings className="w-5 h-5" /> }

  ];






  // Home Page
  const HomePage = () => (

    <div>

      {/* Hero Section */}
      <HeroSection heroData={heroData} loading={loading} />



      {/* Features */}

      <section className="py-4 bg-gradient-to-r from-brand-orange-600 via-blue-500 to-purple-500">

        <div className="max-w-7xl mx-auto px-4">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">

            <div className="flex items-center justify-center gap-2 text-white font-bold">

              <Clock className="w-6 h-6" />

              <span>خدمة 24/7</span>

            </div>

            <div className="flex items-center justify-center gap-2 text-white font-bold">

              <Shield className="w-6 h-6" />

              <span>ضمان شامل</span>

            </div>

            <div className="flex items-center justify-center gap-2 text-white font-bold">

              <Award className="w-6 h-6" />

              <span>جودة عالية</span>

            </div>

            <div className="flex items-center justify-center gap-2 text-white font-bold">

              <TrendingUp className="w-6 h-6" />

              <span>أفضل الأسعار</span>

            </div>

          </div>

        </div>

      </section>



      {/* Services Overview */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-brand-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 md:mb-4 px-4">
              خدماتنا <span className="text-brand-orange-600">المتكاملة</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg px-4">كل ما تحتاجه لسيارتك في مكان واحد</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">

            <div
              onClick={() => setCurrentPage('maintenance')}
              className="bg-gradient-to-br bg-brand-gray-700 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 hover:scale-105 transition-all cursor-pointer border-2 border-transparent hover:border-brand-orange-600 group"
            >
              <div className="bg-brand-orange-600 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:rotate-12 transition">
                <Wrench className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-black" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white mb-2 md:mb-3">الصيانة المنزلية</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-2 sm:mb-3 md:mb-4 line-clamp-2">نأتي إليك أينما كنت</p>
              <button className="text-brand-orange-600 font-semibold flex items-center gap-1 sm:gap-2 group-hover:gap-3 transition-all text-xs sm:text-sm md:text-base">
                المزيد <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>



            <div
              onClick={() => setCurrentPage('accessories')}
              className="bg-gradient-to-br bg-brand-gray-700 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 hover:scale-105 transition-all cursor-pointer border-2 border-transparent hover:border-blue-500 group"
            >
              <div className="bg-blue-500 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:rotate-12 transition">
                <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white mb-2 md:mb-3">الاكسسوارات</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-2 sm:mb-3 md:mb-4 line-clamp-2">أحدث إكسسوارات السيارات</p>
              <button className="text-blue-500 font-semibold flex items-center gap-1 sm:gap-2 group-hover:gap-3 transition-all text-xs sm:text-sm md:text-base">
                تسوق <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>



            <div
              onClick={() => setCurrentPage('parts')}
              className="bg-gradient-to-br bg-brand-gray-700 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 hover:scale-105 transition-all cursor-pointer border-2 border-transparent hover:border-purple-500 group"
            >
              <div className="bg-purple-500 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:rotate-12 transition">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white mb-2 md:mb-3">قطع الغيار</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-2 sm:mb-3 md:mb-4 line-clamp-2">قطع أصلية ومضمونة</p>
              <button className="text-purple-500 font-semibold flex items-center gap-1 sm:gap-2 group-hover:gap-3 transition-all text-xs sm:text-sm md:text-base">
                عرض <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>



            <div
              onClick={() => setCurrentPage('modifications')}
              className="bg-gradient-to-br bg-brand-gray-700 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 hover:scale-105 transition-all cursor-pointer border-2 border-transparent hover:border-red-500 group"
            >
              <div className="bg-red-500 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:rotate-12 transition">
                <Settings className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white mb-2 md:mb-3">التعديلات</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-2 sm:mb-3 md:mb-4 line-clamp-2">تطوير وتحسين احترافي</p>
              <button className="text-red-500 font-semibold flex items-center gap-1 sm:gap-2 group-hover:gap-3 transition-all text-xs sm:text-sm md:text-base">
                ابدأ <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-r from-brand-orange-600 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4 sm:mb-6">
            احصل على خصم 25% على أول خدمة
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-black/80 mb-6 sm:mb-8">
            عرض محدود لفترة محدودة - احجز الآن!
          </p>
          <button className="bg-black text-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold hover:scale-105 transition inline-flex items-center gap-2 sm:gap-3">
            احجز الآن <Award className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </section>
    </div>
  );



  // Maintenance Page (Using Real Data from Firebase)
  const MaintenancePage = () => (
    <div className="min-h-screen bg-brand-gray-900 py-24 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <Wrench className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto text-brand-orange-600 mb-4 sm:mb-6" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 md:mb-4 px-4">خدمة الصيانة المنزلية</h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 px-4">فريقنا المتخصص يأتي إليك في أي مكان وزمان</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange-600"></div>
          </div>
        ) : services.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-brand-gray-700 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 hover:shadow-2xl hover:shadow-brand-orange-600/20 transition cursor-pointer" onClick={() => navigate(`/service/${service.id}`)}>
                <div className="bg-brand-orange-600 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 overflow-hidden">
                  <SmartImage
                    src={service.icon}
                    alt={service.name}
                    size="medium"
                    fallback="🔧"
                    className="text-white"
                  />
                </div>
                <h3 className="text-sm sm:text-lg md:text-2xl font-bold text-white mb-2 line-clamp-2">{service.name}</h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 sm:mb-4 gap-1">
                  <span className="text-brand-orange-600 text-base sm:text-xl md:text-2xl font-bold">{service.price}</span>
                  {service.duration && (
                    <span className="text-gray-400 text-xs sm:text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" /> {service.duration}
                    </span>
                  )}
                </div>
                {service.includes && service.includes.length > 0 && (
                  <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4 md:mb-6">
                    {service.includes.slice(0, 2).map((item, i) => (
                      <div key={i} className="flex items-center gap-1 sm:gap-2 text-gray-300">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm line-clamp-1">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/service/${service.id}/book`); }}
                  className="w-full bg-brand-orange-600 hover:bg-brand-orange-500 text-black font-bold py-2 sm:py-2.5 md:py-3 rounded-lg md:rounded-xl transition text-xs sm:text-sm md:text-base"
                >
                  احجز الآن
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400 py-12">
            <Wrench className="w-24 h-24 mx-auto mb-4 opacity-50" />
            <p className="text-xl">لا توجد خدمات متاحة حالياً</p>
          </div>
        )}
      </div>
    </div>
  );



  // Accessories Page (Using Real Data from Firebase)
  const AccessoriesPage = () => {
    const categoryProducts = products.filter(p => p.categoryName?.toLowerCase().includes('اكسسوار') || p.type === 'accessory');

    return (
      <div className="min-h-screen bg-brand-gray-900 py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <ShoppingBag className="w-20 h-20 mx-auto text-blue-500 mb-6" />
            <h1 className="text-5xl font-bold text-white mb-4">متجر الاكسسوارات</h1>
            <p className="text-xl text-gray-400">أفضل إكسسوارات السيارات بأسعار منافسة</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : categoryProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {categoryProducts.map((item) => (
                <div key={item.id} className="bg-brand-gray-700 rounded-xl md:rounded-2xl overflow-hidden hover:scale-105 transition group">
                  <div className="h-48 w-full overflow-hidden">
                    <SmartImage
                      src={item.image}
                      alt={item.name}
                      size="large"
                      fallback="🛍️"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{item.name}</h3>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-blue-500">{item.price}</span>
                      {item.brand && <span className="text-sm text-gray-400">{item.brand}</span>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/product/${item.id}`)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2">
                        <Eye className="w-4 h-4" /> التفاصيل
                      </button>
                      <button onClick={() => addToCart(item)} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2">
                        <ShoppingCart className="w-4 h-4" /> أضف
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <Package className="w-24 h-24 mx-auto mb-4 opacity-50" />
              <p className="text-xl">لا توجد منتجات حالياً</p>
            </div>
          )}
        </div>
      </div>
    );
  };



  // Parts Page (Using Real Data from Firebase)
  const PartsPage = () => {
    const partsProducts = products.filter(p => p.categoryName?.toLowerCase().includes('قطع') || p.type === 'part');

    return (
      <div className="min-h-screen bg-brand-gray-900 py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Package className="w-20 h-20 mx-auto text-purple-500 mb-6" />
            <h1 className="text-5xl font-bold text-white mb-4">متجر قطع الغيار</h1>
            <p className="text-xl text-gray-400">قطع غيار أصلية بضمان الوكالة</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : partsProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {partsProducts.map((item) => (
                <div key={item.id} className="bg-brand-gray-700 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 hover:shadow-2xl hover:shadow-purple-500/20 transition">
                  <div className="h-32 w-full rounded-xl overflow-hidden mb-4">
                    <SmartImage
                      src={item.image}
                      alt={item.name}
                      size="large"
                      fallback="⚙️"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.name}</h3>
                  {item.brand && (
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <Shield className="w-4 h-4" />
                      <span>العلامة: {item.brand}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-purple-500">{item.price}</span>
                    <span className="bg-green-500/20 text-green-500 px-3 py-1 rounded-full text-sm font-semibold">
                      {item.stock || 'متوفر'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => navigate(`/product/${item.id}`)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => addToCart(item)} className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <Package className="w-24 h-24 mx-auto mb-4 opacity-50" />
              <p className="text-xl">لا توجد قطع غيار حالياً</p>
            </div>
          )}
        </div>
      </div>
    );
  };



  // Modifications Page (Using Real Data from Firebase)
  const ModificationsPage = () => {
    const [modifications, setModifications] = useState([]);
    const [modsLoading, setModsLoading] = useState(true);

    useEffect(() => {
      const loadMods = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, COLLECTIONS.MODIFICATIONS));
          const mods = [];
          querySnapshot.forEach((doc) => {
            mods.push({ id: doc.id, ...doc.data() });
          });
          setModifications(mods);
        } catch (error) {
          console.error('Error loading modifications:', error);
        } finally {
          setModsLoading(false);
        }
      };
      loadMods();
    }, []);

    return (
      <div className="min-h-screen bg-brand-gray-900 py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Settings className="w-20 h-20 mx-auto text-red-500 mb-6" />
            <h1 className="text-5xl font-bold text-white mb-4">تعديل وتطوير السيارات</h1>
            <p className="text-xl text-gray-400">حول سيارتك لتحفة فنية رياضية</p>
          </div>

          {modsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
          ) : modifications.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
              {modifications.map((mod) => (
                <div key={mod.id} className="bg-brand-gray-700 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 hover:shadow-2xl hover:shadow-red-500/20 transition">
                  <div className="flex items-start gap-6">
                    <div className="bg-red-500 w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 text-5xl">
                      {String(mod.image || '⚙️')}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-2">{String(mod.name || '')}</h3>
                      {mod.description && <p className="text-gray-400 mb-4">{String(mod.description)}</p>}
                      <span className="text-3xl font-bold text-red-500">{String(mod.price || '')}</span>
                    </div>
                  </div>

                  {mod.features && mod.features.length > 0 && (
                    <div className="mt-6 space-y-3">
                      {mod.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3 text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>{String(feature)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => navigate(`/modification/${mod.id}`)}
                    className="w-full mt-6 bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-xl transition"
                  >
                    احجز استشارة مجانية
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <Settings className="w-24 h-24 mx-auto mb-4 opacity-50" />
              <p className="text-xl">لا توجد خدمات تعديل حالياً</p>
            </div>
          )}
        </div>
      </div>
    );
  };



  return (

    <div className="min-h-screen bg-black text-white">

      {/* Navigation */}

      <NavBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />



      {/* Page Content */}

      <main className="pt-20">

        {currentPage === 'home' && (
          <>
            <HomePage />
            <section id="categories-section">
              <CategoriesSection key="categories-static" />
            </section>
          </>
        )}

        {currentPage === 'maintenance' && <MaintenancePage />}

        {currentPage === 'accessories' && <AccessoriesPage />}

        {currentPage === 'parts' && <PartsPage />}

        {currentPage === 'modifications' && <ModificationsPage />}

      </main>



      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12 py-8 sm:py-12 md:py-16">

            {/* Company Info */}
            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="/3.png" alt="شعار الموقع" className="w-12 h-12 object-contain" />
                <h3 className="text-2xl font-bold text-white tracking-wide">LUXURY CAR</h3>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6 text-sm">
                نقدم أفضل خدمات الصيانة والتعديل وقطع الغيار لسيارتك بجودة عالية واحترافية
              </p>

              {/* Social Media */}
              <div className="flex gap-3">
                <a href="#" className="group bg-brand-gray-700 hover:bg-brand-orange-600 p-3 rounded-xl transition-all duration-300 transform hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </a>
                <a href="#" className="group bg-brand-gray-700 hover:bg-brand-orange-600 p-3 rounded-xl transition-all duration-300 transform hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                </a>
                <a href="#" className="group bg-brand-gray-700 hover:bg-brand-orange-600 p-3 rounded-xl transition-all duration-300 transform hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" /></svg>
                </a>
              </div>
            </div>



            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2 border-b border-gray-800 pb-3">
                <Award className="w-5 h-5 text-brand-orange-600" />
                روابط سريعة
              </h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => setCurrentPage('home')}
                    className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                    الرئيسية
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage('maintenance')}
                    className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                    الصيانة
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage('accessories')}
                    className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                    الاكسسوارات
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage('parts')}
                    className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                    قطع الغيار
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage('modifications')}
                    className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                    التعديلات
                  </button>
                </li>
              </ul>
            </div>



            {/* Services */}
            <div>
              <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2 border-b border-gray-800 pb-3">
                <Wrench className="w-5 h-5 text-brand-orange-600" />
                الخدمات
              </h4>
              <ul className="space-y-3">
                <li className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 cursor-pointer flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                  فحص شامل
                </li>
                <li className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 cursor-pointer flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                  تغيير زيت
                </li>
                <li className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 cursor-pointer flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                  صيانة دورية
                </li>
                <li className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 cursor-pointer flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                  قطع أصلية
                </li>
                <li className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200 cursor-pointer flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-brand-orange-600 transition-colors"></span>
                  تعديل محترف
                </li>
              </ul>
            </div>



            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2 border-b border-gray-800 pb-3">
                <Phone className="w-5 h-5 text-brand-orange-600" />
                اتصل بنا
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group">
                  <div className="bg-brand-gray-700 group-hover:bg-brand-orange-600 p-2 rounded-lg transition-colors">
                    <Phone className="w-4 h-4 text-brand-orange-600 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">هاتف</p>
                    <p className="text-sm">+966-XXX-XXXX</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group">
                  <div className="bg-brand-gray-700 group-hover:bg-brand-orange-600 p-2 rounded-lg transition-colors">
                    <Mail className="w-4 h-4 text-brand-orange-600 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">البريد</p>
                    <p className="text-sm break-all">info@luxurycar.sa</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group">
                  <div className="bg-brand-gray-700 group-hover:bg-brand-orange-600 p-2 rounded-lg transition-colors">
                    <MapPin className="w-4 h-4 text-brand-orange-600 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">العنوان</p>
                    <p className="text-sm">الرياض، السعودية</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group">
                  <div className="bg-brand-gray-700 group-hover:bg-brand-orange-600 p-2 rounded-lg transition-colors">
                    <Clock className="w-4 h-4 text-brand-orange-600 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">ساعات العمل</p>
                    <p className="text-sm">24/7 - متاحون دائماً</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>



          {/* Bottom Bar */}
          <div className="border-t border-gray-800 py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center sm:text-right flex items-center gap-2">
                <span className="text-brand-orange-600">©</span>
                2025 <span className="font-bold text-white">Luxury Car</span> - جميع الحقوق محفوظة
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
                <button className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200">
                  سياسة الخصوصية
                </button>
                <span className="text-gray-700">|</span>
                <button className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200">
                  الشروط والأحكام
                </button>
                <span className="text-gray-700">|</span>
                <button className="text-gray-400 hover:text-brand-orange-600 transition-colors duration-200">
                  سياسة الاسترجاع
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>



      {/* Floating WhatsApp Button */}

      <a

        href="https://wa.me/966XXXXXXXX"

        target="_blank"

        rel="noopener noreferrer"

        className="fixed bottom-6 left-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition z-50 group"

      >

        <Phone className="w-6 h-6" />

        <span className="absolute right-full mr-3 bg-black text-white px-4 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition">

          تواصل معنا عبر واتساب

        </span>

      </a>



      {/* Scroll to Top Button */}

      <button

        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}

        className="fixed bottom-6 right-6 bg-brand-orange-600 hover:bg-brand-orange-500 text-black p-4 rounded-full shadow-2xl hover:scale-110 transition z-50"

      >

        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">

          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />

        </svg>

      </button>

    </div>

  );

};



export default LuxuryCarWebsite;
